import { ICommonSettings, IFirebaseSetting, IImageLibraryDto } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const FIREBASE_SETTINGS_SEARCH_TERMS: string[] = [
  "Sender Id",
  "Server Key",
  "Service Account Json",
  "Notification Certificate"
];

export interface IConfigFirebaseSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingFirebase?: IFirebaseSetting;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  fileName: string;
  serviceAccountJSONFileName: string;
}

export interface IConfigFirebaseSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFirebaseId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFirebaseSettings extends React.PureComponent<
  IConfigFirebaseSettingsProps,
  IConfigFirebaseSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFirebaseSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingFirebase: undefined,
      allImage: [],
      imageSearchText: "",
      fileName: "",
      serviceAccountJSONFileName: ""
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFirebaseSettingsProps) {
    if (prevProps.editingFirebaseId !== this.props.editingFirebaseId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingFirebaseId) {
      const fb = this.props.commonSettings.firebaseSettings?.find(
        (x) => x.id === this.props.editingFirebaseId && !x.isGlobalCommonConfigOnProjectConfig
      );

      this.setState({ editingFirebase: fb }, () => {
        if (fb) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.firebaseName = fb.name;
          fieldsValue.senderId = fb.senderId;
          fieldsValue.senderKey = fb.senderKey;
          fieldsValue.serviceAccountJson = fb.serviceAccountJson;
          fieldsValue.notificationCertificate = fb.notificationCertificate;

          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({
            fileName: fb.notificationCertificate,
            serviceAccountJSONFileName: fb.serviceAccountJson
          });
        }
      });
    } else {
      this.setState({ fileName: "" });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings.firebaseSettings;

    const isSettingAlreadyExist =
      setting?.some(
        (x) => x.name === value && x.id !== this.props.editingFirebaseId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Firebase Setting name is already exist.");
  };

  serverKeyValidator = async (_rule, value) => {
    if (this.state.serviceAccountJSONFileName) return;

    if (!value) throw new Error("Please enter a Server Key or choose a Service Account Json file");
    if (!/^\S+$/.test(value)) throw new Error("Please enter a valid Server Key");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IFirebaseSetting;
    if (this.props.editingFirebaseId) {
      obj = {
        id: this.props.editingFirebaseId,
        name: data.firebaseName,
        senderId: data.senderId?.trim(),
        senderKey: data.senderKey?.trim(),
        notificationCertificate: this.state.fileName,
        serviceAccountJson: this.state.serviceAccountJSONFileName
      };

      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "firebaseSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.firebaseName,
        senderId: data.senderId?.trim(),
        senderKey: data.senderKey?.trim(),
        notificationCertificate: this.state.fileName,
        serviceAccountJson: this.state.serviceAccountJSONFileName
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "firebaseSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteFirebase = () => {
    if (!this.props.editingFirebaseId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFirebaseId || "",
      configKey: "firebaseSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Firebase setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
            This Firebase setting is
            used on ${
              this.props.formInputName[0] === "webConfig" ? "Android" : "Web"
            } section. Please discard this setting
            on other config section before delete.
          `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Firebase Setting"
        secondHeader="Firebase Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFirebaseId ? this.deleteFirebase : undefined}
            deleteTestId="firebaseSettingDeleteButton"
            submitTestId={`firebaseSetting${this.props.editingFirebaseId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingFirebaseId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFirebaseForm"
          />
        }
      >
        <Form
          id="configFirebaseForm"
          name="firebaseSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              this.props.formInputName[0] === "androidConfig"
                ? "firebasesettingspopup_pushnotificationsettings_description"
                : ""
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey={
                this.props.formInputName[0] === "androidConfig"
                  ? "firebasesettingspopup_pushnotificationsettings_name"
                  : ""
              }
            >
              <Form.Item
                name="firebaseName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Firebase Setting name"
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Firebase Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="firebaseSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Sender Id"
              required
              tooltipContentKey="firebasesettingspopup_pushnotificationsettings_teamid"
            >
              <Form.Item
                name="senderId"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Sender Id."
                  },
                  {
                    pattern: /^\S+$/,
                    message: "Please enter a valid Sender Id"
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="firebaseSettingSenderIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Server Key"
              required={!this.state.serviceAccountJSONFileName}
              tooltipContentKey="firebasesettingspopup_pushnotificationsettings_serverkey"
            >
              <Form.Item name="senderKey" rules={[{ validator: this.serverKeyValidator }]}>
                <KuikaInput size="md" data-testid="firebaseSettingSenderKeyInput" />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Service Account Json"
              tooltipContentKey="firebasesettingspopup_pushnotificationsettings_serviceaccountjson"
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".json"
                getImageLibrary={this.getImageLibrary}
                selectedId={
                  this.state.allImage?.find(
                    (x: IImageLibraryDto): boolean => x.fileName === this.state.serviceAccountJSONFileName
                  )?.id
                }
                fileName={this.state.serviceAccountJSONFileName}
                onSelect={(image: IImageLibraryDto): void =>
                  this.setState({ serviceAccountJSONFileName: image.fileName }, (): void => {
                    this.formRef.current?.validateFields().catch((): void => {});
                  })
                }
                onClear={(): void => this.setState({ serviceAccountJSONFileName: "" })}
              />
            </ConfigField>

            <ConfigField
              label="Notification Certificate"
              tooltipContentKey={
                this.props.formInputName[0] === "androidConfig"
                  ? "firebasesettingspopup_pushnotificationsettings_notificationcertificate"
                  : ""
              }
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".json"
                getImageLibrary={this.getImageLibrary}
                selectedId={
                  this.state.allImage?.find((x: IImageLibraryDto): boolean => x.fileName === this.state.fileName)?.id
                }
                fileName={this.state.fileName}
                onSelect={(image: IImageLibraryDto): void => this.setState({ fileName: image.fileName })}
                onClear={(): void => this.setState({ fileName: "" })}
              />
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
